import z, { ZodError } from 'zod'
import { getPlatform } from '@/platform'

const urlsSchema = z.object({ apiURL: z.url(), appURL: z.url() })
export type TURLs = z.infer<typeof urlsSchema>

/** True when `VITE_API_URL` points at typical local dev (loopback, RFC1918, e.g. iPhone hotspot 172.20.10.x). */
function isLocalDevApiHostname(hostname: string): boolean {
  const h = hostname.toLowerCase()
  if (h === 'localhost' || h === '127.0.0.1') {
    return true
  }
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(h)
  if (!m) {
    return false
  }
  const a = Number(m[1]); const b = Number(m[2]); const c = Number(m[3]); const d = Number(m[4])
  if ([a, b, c, d].some(n => n > 255 || Number.isNaN(n))) {
    return false
  }
  if (a === 10) {
    return true
  }
  if (a === 172 && b >= 16 && b <= 31) {
    return true
  }
  if (a === 192 && b === 168) {
    return true
  }
  return false
}

export const getCurrentURLS = (): TURLs => {
  let urls: TURLs
  try {
    const { VITE_API_URL: apiURL, VITE_APP_URL: appURL } = import.meta.env
    urls = urlsSchema.parse({ apiURL, appURL })
  }
  catch (err) {
    if (err instanceof ZodError) {
      throw new TypeError(`please check if VITE_API_URL and VITE_APP_URL are defined`)
    }
    else {
      throw err
    }
  }
  return urls
}

/**
 * When loaded from the Vite dev server (5173 default, 5177 E2E, etc.), use same-origin /api-proxy
 * so all API and WS requests go through Vite's proxy (no cert trust needed). When on Android but
 * not a dev origin, use 10.0.2.2 so the emulator reaches the host.
 */
export function apiURLForPlatform(apiURL: string): string {
  try {
    const u = new URL(apiURL)
    const h = u.hostname
    if (!isLocalDevApiHostname(h)) {
      return apiURL
    }
    // Dev server origin: use proxy so every request (including WebSocket auth) is same-origin.
    if (import.meta.env.DEV && typeof window !== 'undefined' && window.location?.origin) {
      const origin = window.location
      if (isLocalDevApiHostname(origin.hostname) && origin.port !== '') {
        return `${origin.origin}/api-proxy`
      }
    }
    // Android emulator: use 10.0.2.2 so requests reach the host (only when not using proxy).
    if (getPlatform() === 'android') {
      u.hostname = '10.0.2.2'
      return u.toString()
    }
    return apiURL
  }
  catch {
    return apiURL
  }
}

/** Returns API URL with platform override (e.g. 10.0.2.2 on Android). Call after initPlatform() for correct result. */
export function getApiURL(): string {
  const { apiURL } = getCurrentURLS()
  return apiURLForPlatform(apiURL)
}

export default getCurrentURLS
